import "./Ebt.scss";
import Footer from "../../../../components/Footer/Footer";
import Header from "../../../../components/Header/Header";
import ProjectHero from "../../../../templates/ProjectHero/ProjectHero";
import ContentBlock from "../../../../templates/ContentBlock/ContentBlock";
import FullWidthImageSection from "../../../../templates/FullWidthImageSection/FullWidthImageSection";
import ProjectPanelDisplay from "../../../../templates/ProjectPanelDisplay/ProjectPanelDisplay";
import { Helmet } from 'react-helmet';
import Notification from "../../../../components/Notification/Notification";
import CardBlocksSection from "../../../../templates/CardBlocksSection/CardBlocksSection";
import researchInsightCards from "./research-data.json";

const heroImage =
    "https://res.cloudinary.com/dhifyp04n/image/upload/v1783720982/Frame_183542_zcsiee.png";

function Ebt(){
    return(
        <div className="page-layout">
            <Helmet>
                <title>SNAP/EBT Payments - Sony Chen</title>
            </Helmet>

            <Header />
            <main className="page-layout__main">
                <section className="page-layout__project-wrapper">
                    <ProjectHero 
                        tag="Feature release"
                        header="SNAP/EBT Payment Experience"
                        body="Enabling SNAP/EBT payments made discounted surplus food more accessible to shoppers relying on SNAP benefits, helping expand access and reduce affordability barriers."
                        company="Flashfood"
                        role="Product Designer"
                        year="2022"
                        src={heroImage}
                        alt="A preview of Flashfood’s SNAP/EBT payment system"
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Context"
                        body={'Flashfood’s mission is to reduce food waste by connecting shoppers with discounted surplus grocery items. However, customers relying on SNAP benefits were unable to use the platform, creating a major accessibility gap for the very shoppers most likely to benefit from affordable groceries.\n\nAs demand from grocery partners grew alongside expanding regulatory support for online SNAP purchasing, enabling SNAP/EBT payments became a high-priority initiative. To support this launch, we partnered with Forage, an API infrastructure platform built specifically for SNAP/EBT online transactions. Their technical requirements and compliance constraints directly influenced both the product strategy and the end-to-end user experience throughout the project.'}
                    />
                    <div className="page-layout__section-wrapper">
                        <Notification
                            layout="info"
                            title="About Forage:"
                            titleUrl="https://www.joinforage.com/"
                            body="Forage powers online SNAP/EBT transactions through their API infrastructure. The integration introduced technical and compliance constraints, including a required PIN entry flow that needed to feel seamless within the Flashfood experience."
                        />
                    </div>

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="What we learned"
                        body="We conducted a competitive analysis of platforms supporting online SNAP/EBT payments, alongside user interviews with SNAP recipients to better understand friction points and concerns. We also reviewed Forage’s integration documentation early to identify where design flexibility began and ended."
                    />

                    <CardBlocksSection cards={researchInsightCards} />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="End-to-end experience"
                        body="The happy path for the end-to-end SNAP/EBT journey was mapped to identify friction points across the Flashfood and Forage payment flows."
                    />

                    <FullWidthImageSection 
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783719671/Frame_183543_gmyj02.png"
                        variant="image-only"
                        alt="SNAP/EBT flow happy path overview"
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={true}
                        header="Integration touchpoints"
                        body="SNAP/EBT payments required careful integration across multiple parts of the Flashfood app, with Forage handling the regulated payment infrastructure layer."
                        listItems={[
                            "Product eligibility and item availability",
                            "Forage card linking",
                            "Checkout & PIN entry",
                            "Receipt & confirmation"
                        ]}
                    />

                    <ProjectPanelDisplay
                        header="SNAP eligibility & availability"
                        body="Clear SNAP-eligible labeling on product cards helped users understand item eligibility before reaching checkout."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783719348/Frame_183544_ie5law.png"
                        alt="SNAP eligibility and item availability screenshot"
                        isReverse={false}
                    />

                    <ProjectPanelDisplay
                        header="Forage’s regulated flow"
                        body="EBT card linking and checkout were handled through Forage’s regulated flow, including secure PIN entry. The experience was designed to create a seamless and trustworthy transition between Forage and Flashfood throughout the payment journey."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783719338/Frame_183545_rzn1ds.png"
                        alt="Screenshot of the EBT card setup flow"
                        isReverse={true}
                    />

                    <ProjectPanelDisplay
                        header="The split-payment experience"
                        body="Users could choose exactly how much of their SNAP balance to apply toward eligible items, with the remaining amount paid by credit card through the split-payment flow."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783719828/Frame_183546_owrrtn.png"
                        alt="Screenshot of the EBT checkout flow"
                        isReverse={false}
                    />

                    <ProjectPanelDisplay
                        header="Order summary & confirmation"
                        body="Order confirmations clearly separated SNAP and non-SNAP charges, giving users a transparent breakdown of what was paid with SNAP benefits versus their credit card within the split-payment flow."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783719938/Frame_183548_xbqhpe.png"
                        alt="Screenshot of the SNAP/EBT order summary and confirmation"
                        isReverse={true}
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Wallet & balance integration"
                        body={'As part of the SNAP/EBT initiative, an EBT payment vault was integrated into Flashfood, allowing users to securely save and manage their EBT cards within the app through Forage’s API infrastructure.\n\nThe collaboration also created an opportunity to integrate SNAP balance checking directly into the Flashfood experience, giving users greater visibility and convenience throughout checkout.'}
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783720236/Frame_183550_kuiong.png"
                        alt="Screenshot of the EBT card vault and SNAP balance integration"
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Platform-based design alignment"
                        body="At the time of the project, iOS and Android experiences were maintained through platform-specific design patterns and components. To ensure implementation clarity and consistency across platforms, dedicated mocks were created for both operating systems throughout the SNAP/EBT experience."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783720414/Frame_183551_g61u7r.png"
                        alt="Screenshot of the platform-based design alignment"
                    />

                    <ProjectPanelDisplay
                        header="Designing beyond the happy path"
                        body="While the core experience was built alongside Forage, user testing revealed additional edge cases and unhappy paths that needed to be addressed. These scenarios helped shape clearer recovery states, error messaging, and support flows throughout the checkout experience."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779812712/Frame_183547_tmxshc.png"
                        alt="Screenshot of the designing beyond the happy path"
                        isReverse={true}
                    />

                    <ProjectPanelDisplay
                        header="Support admin refund flow"
                        body="As the sole designer on the project, the scope also included designing the refund workflow within the support admin experience. The updated flow provided support teams with greater flexibility when handling SNAP-related refunds, informed through user testing and job shadowing sessions with internal teams."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779813293/Frame_183553_llnwsb.png"
                        alt="Screenshot of the support admin refund flow"
                        isReverse={false}
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Outcome"
                        body="The project was delivered to development with finalized designs and detailed specifications covering the full SNAP/EBT experience across both Flashfood and Forage integration touchpoints. Designing within Forage’s API and compliance constraints, particularly around card linking and PIN entry, required careful consideration to ensure the experience remained seamless, trustworthy, and cohesive within the Flashfood ecosystem."
                    />

                    <ProjectPanelDisplay
                        header="Surfacing user impact"
                        body="Early in the project, I identified an opportunity to improve how customer savings were surfaced throughout the Flashfood experience. After aligning with the PM, the enhancement was incorporated into the release to better reinforce the value users received through the platform. The UI improvement has remained part of the product through Flashfood’s recent rebrand and redesign."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1783720588/Frame_183554_umr9lk.png"
                        alt="Screenshot of the EBT card vault and SNAP balance integration"                        isReverse={false}
                    />
                        
                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Key takeaways"
                        bodies={[
                            {
                            subheader: "Designing for sensitive payment experiences ✨",
                            body: "SNAP/EBT flows required thoughtful consideration around tone, hierarchy, and language to ensure the experience felt seamless, respectful, and integrated naturally within the existing checkout flow."
                            },
                            {
                            subheader: "Designing around third-party constraints 🔗",
                            body: "Key parts of the payment experience relied on Forage’s regulated infrastructure, requiring careful design decisions around transitions, trust, and system integration across multiple surfaces."
                            },
                            {
                            subheader: "Surfacing information earlier 👀",
                            body: "Introducing SNAP eligibility indicators earlier in the journey, including product listings and cart surfaces, helped reduce uncertainty and improve checkout clarity before payment."
                            }
                        ]}
                    />
                </section>

                <Footer 
                    noBackgroundColor="true"
                />
            </main>
        </div>
    );
};

export default Ebt;